import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SERVICE_OPTIONS, CONTACT, IMAGES } from "@/lib/content";

const EMPTY = {
  name: "", email: "", phone: "", service: SERVICE_OPTIONS[0],
  property_type: "Residential", address: "", preferred_date: "", message: "",
};

export default function BookingForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
      toast.success("Request sent! We'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send request.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#E2E8F0] rounded-sm px-4 py-3 text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#2B4BB8] focus:ring-1 focus:ring-[#2B4BB8] transition";

  return (
    <section id="book" data-testid="booking-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-stretch">
        <motion.div
          initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="relative rounded-sm overflow-hidden bg-[#1A2A5E] text-white p-10 flex flex-col justify-between min-h-[420px]"
        >
          <div className="absolute inset-0 opacity-20">
            <img src={IMAGES.water} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="relative">
            <div className="label-caps mb-4" style={{ color: "#94A3B8" }}>Free Quote</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Book your cleaning or request a quote.
            </h2>
            <p className="mt-4 text-white/80 leading-relaxed max-w-md">
              Tell us about your space and we'll get back to you fast with honest, upfront pricing. No pressure, no hidden fees.
            </p>
          </div>
          <div className="relative mt-8 space-y-3">
            <a href={CONTACT.phoneHref} className="flex items-center gap-3 font-semibold hover:text-[#94A3B8]">
              <Phone className="w-5 h-5 text-[#2B4BB8]" /> {CONTACT.phone}
            </a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 font-semibold hover:text-[#94A3B8]">
              <Mail className="w-5 h-5 text-[#2B4BB8]" /> {CONTACT.email}
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm p-8"
        >
          {done ? (
            <div data-testid="booking-success" className="h-full flex flex-col items-center justify-center text-center py-16">
              <CheckCircle2 className="w-16 h-16 text-[#2B4BB8]" />
              <h3 className="mt-5 font-display text-2xl font-bold text-[#1A2A5E]">Request received!</h3>
              <p className="mt-2 text-[#475569] max-w-sm">Thanks for reaching out. We'll contact you shortly to confirm your cleaning.</p>
              <button onClick={() => setDone(false)} data-testid="booking-another-btn"
                className="mt-6 text-sm font-bold text-[#2B4BB8] underline">Submit another request</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="booking-form" className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="booking-name" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
                <input data-testid="booking-phone" className={inputCls} placeholder="Phone number *" value={form.phone} onChange={set("phone")} />
              </div>
              <input data-testid="booking-email" type="email" className={inputCls} placeholder="Email address *" value={form.email} onChange={set("email")} />
              <div className="grid sm:grid-cols-2 gap-4">
                <select data-testid="booking-service" className={inputCls} value={form.service} onChange={set("service")}>
                  {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select data-testid="booking-property" className={inputCls} value={form.property_type} onChange={set("property_type")}>
                  <option>Residential</option>
                  <option>Commercial</option>
                  <option>Move-Out / Rental</option>
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="booking-address" className={inputCls} placeholder="Address / area" value={form.address} onChange={set("address")} />
                <input data-testid="booking-date" type="date" className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} />
              </div>
              <textarea data-testid="booking-message" rows={4} className={inputCls} placeholder="Tell us about the job (rooms, stains, pets, etc.)" value={form.message} onChange={set("message")} />
              <button type="submit" data-testid="booking-submit-button" disabled={loading}
                className="w-full bg-[#1A2A5E] text-white font-bold py-4 rounded-sm hover:bg-[#2B4BB8] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : "Send My Request"}
              </button>
              <p className="text-xs text-[#94A3B8] text-center">We'll never share your details. Expect a reply within one business day.</p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
